type UiActionLike = { type?: string; [key: string]: unknown };

function extractActions(toolArgs?: Record<string, unknown>): UiActionLike[] {
  const raw = toolArgs?.actions ?? toolArgs?.action;
  if (Array.isArray(raw)) {
    return raw.filter((entry): entry is UiActionLike => Boolean(entry) && typeof entry === "object");
  }
  if (raw && typeof raw === "object") {
    return [raw as UiActionLike];
  }
  return [];
}

/**
 * Human-readable thinking-step title for `apply_ui_action` when arguments include UI actions.
 */
export function getApplyUiActionThinkingTitle(
  toolArgs?: Record<string, unknown>,
  options?: { complete?: boolean },
): string | null {
  const actions = extractActions(toolArgs);
  if (actions.length === 0) return null;

  const complete = options?.complete === true;
  const label = (running: string, done: string) => (complete ? done : running);

  const types = new Set(actions.map((action) => String(action.type ?? "").toLowerCase()));

  if (types.has("highlight_manuscript_batch")) {
    const batch = actions.find((action) => action.type === "highlight_manuscript_batch");
    const passageCount = Array.isArray(batch?.passages)
      ? batch.passages.filter((p) => typeof p === "string" && p.trim()).length
      : 0;
    if (passageCount > 1) {
      return label(`Highlighting ${passageCount} passages`, `Highlighted ${passageCount} passages`);
    }
    return label("Highlighting manuscript", "Highlighted manuscript");
  }

  if (types.has("highlight_manuscript")) {
    const single = actions.find((action) => action.type === "highlight_manuscript");
    if (single?.match_all === true) {
      return label("Highlighting all matches", "Highlighted all matches");
    }
    return label("Highlighting manuscript", "Highlighted manuscript");
  }

  if (types.has("clear_manuscript_highlights")) {
    return label("Clearing highlights", "Cleared highlights");
  }

  if (types.has("set_agentic_chat_mode")) {
    const modeAction = actions.find((action) => action.type === "set_agentic_chat_mode");
    const mode = typeof modeAction?.mode === "string" ? modeAction.mode.toLowerCase() : "";
    if (mode === "write") return complete ? "Switched to Allow edits" : "Switching to Allow edits";
    if (mode === "chat") return complete ? "Switched to Chat" : "Switching to Chat";
    return label("Switching mode", "Switched mode");
  }

  if (types.has("set_writing_card_count")) {
    const cardAction = actions.find((action) => action.type === "set_writing_card_count");
    const target = typeof cardAction?.target === "string" ? cardAction.target.toLowerCase() : "";
    if (target === "rewrite") return label("Updating Rewrite cards", "Updated Rewrite cards");
    if (target === "write") return label("Updating Write cards", "Updated Write cards");
    return label("Updating card count", "Updated card count");
  }

  if (types.has("set_active_document")) {
    return label("Opening document", "Opened document");
  }

  if (types.has("refresh_project")) {
    return label("Refreshing project", "Refreshed project");
  }

  if (types.has("select_series_outline")) {
    return label("Selecting outline", "Selected outline");
  }

  return null;
}
